import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { syncOutlookStatus } from '../../utils/outlook';
import { setOutlookAuthenticated } from '../../store/auth/authSlice';
import { Box, Stack, Typography } from '@mui/material';
import { HiOutlineClipboardList } from 'react-icons/hi';
import { VscListUnordered } from 'react-icons/vsc';
import { FiHome } from 'react-icons/fi';
import { MdOutlineFolderCopy, MdTaskAlt, MdScheduleSend, MdAutoAwesome } from 'react-icons/md';
import { FiVideo } from 'react-icons/fi';
import { BsFillCalendarPlusFill } from 'react-icons/bs';

import
{
  IoChatbubbleEllipsesOutline,
  IoReaderOutline,
  IoMegaphone,
} from 'react-icons/io5';
import { LiaFileContractSolid } from 'react-icons/lia';
import { BsFolder } from 'react-icons/bs';
import { BiCategoryAlt, BiTerminal } from 'react-icons/bi';
import
{
  RiArrowDownSFill,
  RiArrowUpSFill,
  RiTerminalWindowLine,
  RiContactsLine,
  RiMoneyDollarCircleLine,
  RiBuildingLine,
  RiVideoLine,
} from 'react-icons/ri';
import { MdOutlineAttachEmail } from 'react-icons/md';
import { MdFormatIndentIncrease } from 'react-icons/md';
import useRuntimeData from '../../hooks/useRuntimeData';
import { imageUrl } from '../../config/apiUrl';
import classes from './SideBar.module.css';
import { useBeta } from '../../pages/v2/BetaGate';
import MenuV2 from './MenuV2';
import { Email, EventAvailable, PeopleAltRounded, Phone } from '@mui/icons-material';

const RenderItem = ( { icon, title, subMenu = [], path } ) =>
{
  const active = useLocation()?.pathname;
  const [ subnav, setSubnav ] = useState( false );
  const subActive = subMenu.find( ( item, index ) => item?.path == active );
  const showSubnav = () => setSubnav( !subnav );

  return (
    <>
      <Link
        className={[
          classes?.listItemContainer,
          path == active ? classes?.active : classes?.inactive,
          subActive && classes?.subActive,
          subnav && classes?.marginZero,
        ].join( ' ' )}
        to={path}
        onClick={( event ) =>
        {
          if ( subMenu?.length > 0 )
          {
            event.preventDefault();
            showSubnav( !subnav );
          }
        }}
      >
        {icon}
        <span>{title}</span>
        {subMenu?.length > 0 &&
          ( subnav ? (
            <RiArrowUpSFill
              size={20}
              color={'white'}
              className={classes?.dropDownIcon}
            />
          ) : (
            <RiArrowDownSFill
              size={20}
              color={'white'}
              className={classes?.dropDownIcon}
            />
          ) )}
      </Link>
      {subnav &&
        subMenu.map( ( item, index ) =>
        {
          return (
            <Link
              className={[
                classes?.innerItemContainer,
                item?.path == active && classes?.active,
              ].join( ' ' )}
              key={index}
              to={item?.path}
            >
              {item?.icon}
              <span>{item.label}</span>
            </Link>
          );
        } )}
    </>
  );
};

const SideBar = () =>
{
  const superAdminEmail = process.env.REACT_APP_SUPER_ADMIN_EMAIL;
  const logo = useSelector( ( state ) => state?.companyReducer?.logo );
  const { unreadCallCount, unreadSMSCount, unreadEmailCount, unreadTodoCount } =
    useRuntimeData();

  const {
    user: userData,
    fcmToken,
    access_token: accessToken,
  } = useSelector( ( state ) => state?.authReducer );
  const dispatch = useDispatch();
  // Show Outlook as connected when it is connected on the server (once per login).
  useEffect( () =>
  {
    syncOutlookStatus( accessToken, dispatch, setOutlookAuthenticated );
  }, [ accessToken, dispatch ] );
  const isBroker = userData?.role?.includes( 'broker' );
  const isHR = userData?.role?.includes( 'hr' );
  const isOnlyAdmin = userData?.role?.some( ( item ) => [ 'admin' ].includes( item ) );
  const isOnlySuperAdmin = userData?.role?.includes( 'super-admin' );
  const isPaidClient =
    localStorage.getItem( 'testtest' ) == 'true' ? true : false;
  const allowed = [ superAdminEmail ].includes( userData?.email );
  // The redesigned pages (beta) show only for the people testing them.
  const beta = useBeta();

  // People who see the redesigned pages get the redesigned menu with them.
  if ( beta?.staff )
    return (
      <MenuV2
        logo={logo}
        user={userData}
        unreadTodoCount={unreadTodoCount}
        isPaidClient={isPaidClient}
        allowed={allowed}
        superAdminEmail={superAdminEmail}
      />
    );

  return (
    <div className={classes?.mainContainer}>
      <div className={classes?.logoContainer}>
        <img src={imageUrl( logo )} alt="logo" />
      </div>
      <div className={classes.itemsContainer}>
        <RenderItem
          icon={<FiHome size={20} color={'var(--sidebar-text-color)'} />}
          title={'Home'}
          path={'/'}
        />
        <RenderItem
          icon={<MdAutoAwesome size={20} color={'var(--sidebar-text-color)'} />}
          title={'BBS AI'}
          path={'/bbs-ai'}
        />
        {/* <Box position="relative">
          <RenderItem
            icon={
              <IoChatbubbleEllipsesOutline
                size={20}
                color={'var(--sidebar-text-color)'}
              />
            }
            title={'Contacts'}
            path={'/contacts'}
          />
          {unreadCallCount + unreadEmailCount + unreadSMSCount > 0 ? (
            <Stack
              bgcolor="white"
              borderRadius={9999}
              right={0}
              top={10}
              position="absolute"
              width={20}
              height={20}
              alignItems="center"
              justifyContent="center"
            >
              <Typography component="span" color="primary" fontSize={12}>
                {unreadCallCount + unreadEmailCount + unreadSMSCount}
              </Typography>
            </Stack>
          ) : (
            <></>
          )}
        </Box> */}
        <RenderItem
          icon={
            <RiContactsLine size={20} color={'var(--sidebar-text-color)'} />
          }
          title={'Contacts'}
          path={'/contacts-new'}
        />
        <RenderItem
          icon={
            <VscListUnordered size={20} color={'var(--sidebar-text-color)'} />
          }
          title={'Listings'}
          path="/listings"
        />
        {beta?.staff && ( isOnlySuperAdmin || isOnlyAdmin || isBroker ) && (
          <RenderItem
            icon={
              <VscListUnordered size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'Listings (new)'}
            path="/v2/listings"
          />
        )}
        {beta?.staff && ( isOnlySuperAdmin || isOnlyAdmin || isBroker ) && (
          <RenderItem
            icon={
              <VscListUnordered size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'Offers (new)'}
            path="/v2/offers"
          />
        )}
        {beta?.staff && (
          <RenderItem
            icon={
              <VscListUnordered size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'To-Do (new)'}
            path="/v2/todo"
          />
        )}
        {beta?.staff && ( isOnlySuperAdmin || isOnlyAdmin || isBroker ) && (
          <RenderItem
            icon={
              <VscListUnordered size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'Commission (new)'}
            path="/v2/commission"
          />
        )}
        {beta?.staff && ( isOnlySuperAdmin || isOnlyAdmin || isBroker ) && (
          <RenderItem
            icon={
              <VscListUnordered size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'Reports (new)'}
            path="/v2/reports"
          />
        )}
        {beta?.staff && ( isOnlySuperAdmin || isOnlyAdmin || isBroker ) && (
          <RenderItem
            icon={
              <VscListUnordered size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'New leads (new)'}
            path="/v2/intake"
          />
        )}
        {beta?.staff && ( isOnlySuperAdmin || isOnlyAdmin || isBroker ) && (
          <RenderItem
            icon={
              <VscListUnordered size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'Sellers (new)'}
            path="/v2/sellers"
          />
        )}
        {beta?.staff && ( isOnlySuperAdmin || isOnlyAdmin || isBroker ) && (
          <RenderItem
            icon={
              <VscListUnordered size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'Comps (new)'}
            path="/v2/comps"
          />
        )}
        {beta?.staff && ( isOnlySuperAdmin || isOnlyAdmin || isBroker ) && (
          <RenderItem
            icon={
              <VscListUnordered size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'Partners (new)'}
            path="/v2/partners"
          />
        )}
        {beta?.staff && ( isOnlySuperAdmin || isOnlyAdmin || isBroker ) && (
          <RenderItem
            icon={
              <VscListUnordered size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'Buyer alerts (new)'}
            path="/v2/buyer-alerts"
          />
        )}
        {!isHR && (
          <RenderItem
            icon={
              <HiOutlineClipboardList
                size={20}
                color={'var(--sidebar-text-color)'}
              />
            }
            title={'Interests'}
            path={'/interests'}
          />
        )}
        <RenderItem
          icon={
            <EventAvailable size={20} color={'var(--sidebar-text-color)'} />
          }
          title={'Events'}
          path={'/zoom'}
        />
        {/* <RenderItem
          icon={<Email size={20} color={'var(--sidebar-text-color)'} />}
          title={'Emails'}
          path={'/leads/emails'}
        /> */}
        {/* <RenderItem
          icon={<Phone size={20} color={'var(--sidebar-text-color)'} />}
          title={'Phone'}
          path={'/leads/phone'}
        /> */}
        <RenderItem
          icon={
            <PeopleAltRounded size={20} color={'var(--sidebar-text-color)'} />
          }
          title={'Leads'}
          path={'/leads'}
        />
        <RenderItem
          icon={<BiTerminal size={20} color={'var(--sidebar-text-color)'} />}
          title={'Projects'}
          path={'/project-task-manager'}
        />
        {!isHR && (
          <RenderItem
            icon={<BsFolder size={20} color={'var(--sidebar-text-color)'} />}
            title={'Deal Rooms'}
            path={'/team-folder'}
          />
        )}
        {!isHR && (
          <Box position="relative">
            <RenderItem
              icon={<MdTaskAlt size={22} color={'var(--sidebar-text-color)'} />}
              title={'To-Do'}
              path={'/todo'}
            />
            {unreadTodoCount > 0 ? (
              <Stack
                bgcolor="white"
                borderRadius={9999}
                right={0}
                top={10}
                position="absolute"
                width={20}
                height={20}
                alignItems="center"
                justifyContent="center"
              >
                <Typography component="span" color="primary" fontSize={12}>
                  {unreadTodoCount}
                </Typography>
              </Stack>
            ) : (
              <></>
            )}
          </Box>
        )}


        <RenderItem
          icon={
            <RiBuildingLine size={20} color={'var(--sidebar-text-color)'} />
          }
          title={'Entities'}
          path={'/entities'}
        />


        <RenderItem
          icon={
            <MdOutlineFolderCopy
              size={20}
              color={'var(--sidebar-text-color)'}
            />
          }
          title={'Documents'}
          path={'/documents'}
        />



        {( isPaidClient || allowed ) && ( isOnlyAdmin || isBroker ) && (
          <RenderItem
            icon={<IoMegaphone size={18} color={'var(--sidebar-text-color)'} />}
            title={'Marketing'}
            path={'/marketing'}
          />
        )}
        {isOnlySuperAdmin && (
          <RenderItem
            icon={
              <MdOutlineAttachEmail
                size={20}
                color={'var(--sidebar-text-color)'}
              />
            }
            title={'Automated Emails & SMS'}
            path={'/automated-emails'}
          />
        )}

        {allowed && (
          <RenderItem
            icon={
              <LiaFileContractSolid
                size={20}
                color={'var(--sidebar-text-color)'}
              />
            }
            title={'Contracts'}
            path={'/contract-templates'}
          />
        )}

        {/* Cruds */}
        {isOnlyAdmin && (
          <RenderItem
            icon={
              <IoReaderOutline size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'CRUDS'}
            path="#"
            subMenu={[
              {
                label: 'Categories',
                path: '/categories',
                icon: (
                  <BiCategoryAlt
                    size={18}
                    color={'var(--sidebar-text-color)'}
                  />
                ),
              },
              {
                label: 'Industries',
                path: '/industries',
                icon: (
                  <MdFormatIndentIncrease
                    size={18}
                    color={'var(--sidebar-text-color)'}
                  />
                ),
              },
              {
                label: 'Deadline Holidays',
                path: '/deadline-holidays',
                icon: (
                  <EventAvailable
                    sx={{ fontSize: 18, color: 'var(--sidebar-text-color)' }}
                  />
                ),
              },
            ]}
          />
        )}
        {userData?.role.includes( 'super-admin' ) && (
          <RenderItem
            icon={
              <RiContactsLine size={20} color={'var(--sidebar-text-color)'} />
            }
            title={'Contact Management'}
            path={'/contact-management'}
          />
        )}
        {( ( ( isOnlySuperAdmin || isBroker ) && isPaidClient ) || allowed ) && (
          <RenderItem
            icon={
              <RiMoneyDollarCircleLine
                size={20}
                color={'var(--sidebar-text-color)'}
              />
            }
            title={'Commission'}
            path={'/commission'}
          />
        )}
        {userData?.email == superAdminEmail ? (
          <RenderItem
            icon={
              <RiTerminalWindowLine
                size={20}
                color={'var(--sidebar-text-color)'}
              />
            }
            title={'Token Management'}
            path={'/token-management'}
          />
        ) : (
          <></>
        )}
        <RenderItem
          icon={<RiVideoLine size={20} color={'var(--sidebar-text-color)'} />}
          title={'Support Center'}
          path={'/support-center'}
        />
        <RenderItem
          icon={
            <MdScheduleSend size={20} color={'var(--sidebar-text-color)'} />
          }
          title={'Trigger Emails'}
          path={'/trigger-emails'}
        />
      </div>
    </div>
  );
};

export default SideBar;
